import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart, Search, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopShopOpen, setDesktopShopOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setDesktopShopOpen(false);
  }, [location.pathname]);
  const navLinks = [{
    name: 'Lawn Mowers',
    path: '/category/lawn-mowers'
  }, {
    name: 'Snow Blowers',
    path: '/category/snow-blowers'
  }, {
    name: 'Leaf Blowers',
    path: '/category/leaf-blowers'
  }, {
    name: 'Bundles',
    path: '/category/bundles'
  }, {
    name: 'Modules & Accessories',
    path: '/category/modules-accessories'
  }];
  return <>
      {/* Announcement Bar */}
      <div className="bg-yarbo-dark text-white text-center py-2 px-4 text-sm font-medium" data-id="element-80">
        Michigan Yarbo Sales, Demos & Setup Help —{' '}
        <Link to="/contact" className="text-yarbo-yellow hover:underline" data-id="element-81">
          Request a Quote Today
        </Link>
      </div>

      {/* Main Header */}
      <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-3' : 'bg-white py-5 border-b border-gray-100'}`} data-id="element-82">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-83">
          <div className="flex justify-between items-center" data-id="element-84">
            {/* Logo */}
            <Link to="/" className="flex flex-col" data-id="element-85">
              <span className="text-2xl font-extrabold tracking-tight text-yarbo-dark leading-none" data-id="element-86">
                Yarbo Michigan
              </span>
              <span className="text-xs font-medium text-gray-500 tracking-wide mt-1" data-id="element-87">
                by Merem Robotics
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8" data-id="element-88">
              <div className="relative" onMouseEnter={() => setDesktopShopOpen(true)} onMouseLeave={() => setDesktopShopOpen(false)} data-id="element-89">
                <Link to="/shop" className="flex items-center text-gray-700 hover:text-yarbo-dark font-medium py-2" data-id="element-90">
                  Shop <ChevronDown className="ml-1 w-4 h-4" data-id="element-91" />
                </Link>

                {/* Mega Menu Dropdown */}
                <AnimatePresence data-id="element-92">
                  {desktopShopOpen && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} transition={{
                  duration: 0.2
                }} className="absolute top-full left-0 w-64 bg-white shadow-xl rounded-lg border border-gray-100 py-4 mt-2" data-id="element-93">
                      <div className="px-4 pb-2 mb-2 border-b border-gray-100" data-id="element-94">
                        <Link to="/shop" className="font-bold text-yarbo-dark hover:text-yarbo-yellow" data-id="element-95">
                          Shop All Products
                        </Link>
                      </div>
                      {navLinks.map(link => <Link key={link.name} to={link.path} className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-yarbo-dark" data-id="element-96">
                          {link.name}
                        </Link>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              <Link to="/installation-demos" className="text-gray-700 hover:text-yarbo-dark font-medium" data-id="element-97">
                Installation & Demos
              </Link>
              <Link to="/faq" className="text-gray-700 hover:text-yarbo-dark font-medium" data-id="element-98">
                FAQ
              </Link>
              <Link to="/contact" className="text-gray-700 hover:text-yarbo-dark font-medium" data-id="element-99">
                Contact
              </Link>
            </nav>

            {/* Actions */}
            <div className="hidden lg:flex items-center space-x-6" data-id="element-100">
              <button className="text-gray-600 hover:text-yarbo-dark" data-id="element-101">
                <Search className="w-5 h-5" data-id="element-102" />
              </button>
              <button className="text-gray-600 hover:text-yarbo-dark relative" data-id="element-103">
                <ShoppingCart className="w-5 h-5" data-id="element-104" />
                <span className="absolute -top-2 -right-2 bg-yarbo-yellow text-yarbo-dark text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center" data-id="element-105">
                  0
                </span>
              </button>
              <Link to="/contact" className="btn-primary py-2 px-5 text-sm" data-id="element-106">
                Request a Quote
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center space-x-4 lg:hidden" data-id="element-107">
              <button className="text-gray-600" data-id="element-108">
                <ShoppingCart className="w-6 h-6" data-id="element-109" />
              </button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-yarbo-dark p-2 -mr-2" data-id="element-110">
                {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-111" /> : <Menu className="w-6 h-6" data-id="element-112" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence data-id="element-113">
          {mobileMenuOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="lg:hidden bg-white border-b border-gray-100 overflow-hidden" data-id="element-114">
              <div className="px-4 pt-2 pb-6 space-y-1" data-id="element-115">
                <Link to="/shop" className="block px-3 py-3 text-base font-bold text-yarbo-dark border-b border-gray-100" data-id="element-116">
                  Shop All Products
                </Link>
                {navLinks.map(link => <Link key={link.name} to={link.path} className="block px-3 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-yarbo-dark" data-id="element-117">
                    {link.name}
                  </Link>)}
                <div className="pt-4 mt-2 border-t border-gray-100" data-id="element-118">
                  <Link to="/installation-demos" className="block px-3 py-3 text-base font-medium text-gray-700" data-id="element-119">
                    Installation & Demos
                  </Link>
                  <Link to="/faq" className="block px-3 py-3 text-base font-medium text-gray-700" data-id="element-120">
                    FAQ
                  </Link>
                  <Link to="/contact" className="block px-3 py-3 text-base font-medium text-gray-700" data-id="element-121">
                    Contact
                  </Link>
                </div>
                <div className="pt-4 px-3" data-id="element-122">
                  <Link to="/contact" className="w-full btn-primary justify-center" data-id="element-123">
                    Request a Quote
                  </Link>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </header>
    </>;
}